/*
  Domínio das maquininhas: bandeiras, linhas da tabela de taxas e a regra de agrupamento.

  Uma maquininha atende várias bandeiras, cada uma com a própria tabela. `brands` é a lista
  ordenada das bandeiras (as colunas da tela) e `fees[bandeira] = { pix, debit, credit }`, em %,
  onde `credit` tem 12 posições (índice 0 = à vista, índice 1 = 2x ... índice 11 = 12x). `null` é
  "não informado" e `0` é "grátis" — as duas coisas aparecem diferente na tela.

  A ordem vem de `brands` e não de `fees`: o MySQL reordena as chaves de uma coluna JSON.

  Uma bandeira é a chave da tabela: as comuns usam o `value` de COMMON_BRANDS e as demais, o
  nome que o salão digitou.

  Bandeiras **vinculadas** (`linkedBrands`) dividem a mesma taxa: editar uma célula de uma
  delas grava o valor em todas, para o salão não digitar a mesma tabela bandeira por bandeira.

  Pix não depende de bandeira (ao contrário de débito/crédito), então na tela ele é um campo
  único da maquininha. A API e os apps mobile, porém, ainda guardam e esperam `pix` dentro de
  cada bandeira — por isso ele continua existindo em `fees[bandeira].pix`, só que repetido com o
  mesmo valor em todas as bandeiras (`withPixFee`/`pixFeeOf` fazem essa ponte), em vez de editável
  bandeira por bandeira.
*/
export const MAX_INSTALLMENTS = 12
export const MAX_BRAND_NAME = 32

export const COMMON_BRANDS = [
  { value: 'visa', label: 'Visa' },
  { value: 'mastercard', label: 'MasterCard' },
  { value: 'elo', label: 'Elo' },
  { value: 'amex', label: 'American Express' }
]

// Pix não é uma coluna da tabela de bandeiras: é um campo único da maquininha (ver `pixFeeOf`).
export const PIX_ROW = { id: 'pix', label: 'Pix', field: 'pix' }
export const DEFAULT_PIX_FEE = 0

export const FEE_ROWS = [
  { id: 'debit', label: 'Débito', field: 'debit' },
  { id: 'credit-1', label: 'Crédito à vista', field: 'credit', index: 0 },
  ...Array.from({ length: MAX_INSTALLMENTS - 1 }, (_, position) => ({
    id: `credit-${position + 2}`,
    label: `${position + 2}x`,
    field: 'credit',
    index: position + 1
  }))
]

// Maquininha "virtual", sempre disponível na comanda e na tela de cadastro: serve a quem ainda não
// cadastrou as suas. Não existe na API (sem id) e a tabela de taxas é toda zero.
export const UNREGISTERED_MACHINE = {
  id: null,
  name: 'Maquininha sem registro',
  brands: COMMON_BRANDS.map(brand => brand.value),
  fees: Object.fromEntries(COMMON_BRANDS.map(brand => [
    brand.value, { pix: 0, debit: 0, credit: Array(MAX_INSTALLMENTS).fill(0) }
  ]))
}

// Opção do Pix para quem recebeu fora de maquininha (direto na conta bancária). Só existe na
// tela: ao fechar a comanda vira `cardMachine: null`, ver `withoutMachineSentinel`.
export const NO_MACHINE_VALUE = 'none'
export const NO_MACHINE_OPTION = { label: 'Sem maquininha', value: NO_MACHINE_VALUE }

const emptyBrandFees = () => ({ pix: null, debit: null, credit: Array(MAX_INSTALLMENTS).fill(null) })

const cloneBrandFees = brandFees => ({ ...brandFees, credit: [...brandFees.credit] })

export function brandLabel(brand) {
  return COMMON_BRANDS.find(common => common.value === brand)?.label ?? brand
}

// Texto digitado em "Outro" => chave da bandeira. Quem digitou "visa" ou "Master Card" cai na
// bandeira comum, em vez de criar uma segunda coluna com o mesmo nome.
export function brandFromText(text) {
  const name = String(text ?? '').trim().replace(/\s+/g, ' ').slice(0, MAX_BRAND_NAME)
  if (!name) return null

  const simple = value => value.toLowerCase().replace(/\s/g, '')
  const common = COMMON_BRANDS.find(brand => simple(brand.value) === simple(name) || simple(brand.label) === simple(name))

  return common ? common.value : name
}

export function emptyFees() {
  return {}
}

// Só as bandeiras da maquininha, na ordem de `brands`, com as parcelas que a API não devolveu
// completadas. Um cadastro que não guardou bandeiras (taxas soltas, sem `brands`) vem sem colunas.
export function normalizeFees(fees, brands = []) {
  return Object.fromEntries(brands.map(brand => {
    const saved = fees?.[brand]
    const credit = Array.from({ length: MAX_INSTALLMENTS }, (_, index) => saved?.credit?.[index] ?? null)

    return [brand, { pix: saved?.pix ?? null, debit: saved?.debit ?? null, credit }]
  }))
}

export function getFee(fees, brand, row) {
  const brandFees = fees[brand]
  return row.field === 'credit' ? brandFees.credit[row.index] : brandFees[row.field]
}

// A taxa de Pix da maquininha: a primeira informada, na ordem de `brands`. Sem nenhuma bandeira
// com valor (inclusive uma maquininha ainda sem bandeira nenhuma), conta como `DEFAULT_PIX_FEE`.
export function pixFeeOf(fees, brands = []) {
  const found = brands.map(brand => fees[brand]?.pix).find(value => value !== null && value !== undefined)
  return found ?? DEFAULT_PIX_FEE
}

// Grava a mesma taxa de Pix em todas as bandeiras da maquininha, para a API e os apps mobile
// continuarem enxergando `fees[bandeira].pix` como hoje, mesmo a tela só tendo um campo.
export function withPixFee(fees, value) {
  return Object.fromEntries(
    Object.entries(fees).map(([brand, brandFees]) => [brand, { ...brandFees, pix: value }])
  )
}

function withFee(brandFees, row, value) {
  if (row.field !== 'credit') return { ...brandFees, [row.field]: value }

  return { ...brandFees, credit: brandFees.credit.map((fee, index) => index === row.index ? value : fee) }
}

// Grava `value` na bandeira e, se ela estiver vinculada, em todas as vinculadas.
export function setFee(fees, brand, row, value, linkedBrands = []) {
  const targets = linkedBrands.includes(brand) ? linkedBrands : [brand]

  return {
    ...fees,
    ...Object.fromEntries(targets.map(target => [target, withFee(fees[target], row, value)]))
  }
}

// Marcar uma bandeira junta-a ao grupo e já copia para ela a tabela do grupo.
export function linkBrand(fees, brand, linkedBrands) {
  const [reference] = linkedBrands

  return {
    linkedBrands: [...linkedBrands, brand],
    fees: reference ? { ...fees, [brand]: cloneBrandFees(fees[reference]) } : fees
  }
}

export function unlinkBrand(brand, linkedBrands) {
  return linkedBrands.filter(linked => linked !== brand)
}

// Nova coluna, vazia, no fim. Repetir uma bandeira que já está na tabela não faz nada.
export function addBrand(brands, fees, brand) {
  if (brands.includes(brand)) return { brands, fees }

  return { brands: [...brands, brand], fees: { ...fees, [brand]: emptyBrandFees() } }
}

// Tira a coluna e a desvincula; com menos de duas bandeiras não há o que agrupar.
export function removeBrand(brands, fees, linkedBrands, brand) {
  const remaining = brands.filter(item => item !== brand)
  const remainingFees = { ...fees }
  delete remainingFees[brand]

  return {
    brands: remaining,
    fees: remainingFees,
    linkedBrands: remaining.length > 1 ? unlinkBrand(brand, linkedBrands) : []
  }
}

// '' => null (não informado); texto inválido => NaN.
export function parsePercentage(text) {
  const clean = String(text ?? '').replace('%', '').replace(',', '.').trim()

  return clean === '' ? null : Number(clean)
}

export function formatPercentage(value) {
  if (value === null || value === undefined) return ''
  if (value === 0) return 'Grátis'

  return `${String(value).replace('.', ',')}%`
}

export function editablePercentage(value) {
  return value === null || value === undefined ? '' : String(value).replace('.', ',')
}

/*
  Prazo de recebimento: em quantos dias corridos depois da venda a maquininha repassa o dinheiro.
  `{ pix, debit, credit: { mode, days } }`. No crédito, 'installments' faz a parcela k cair em
  `days × k` e 'lump' faz o valor todo cair de uma vez em `days`. É o mesmo padrão que a API usa
  na projeção quando a maquininha não informa o prazo (`DEFAULT_SETTLEMENT` em cutilei-api), e os
  limites abaixo são os que o `CardMachinesController` aceita.
*/
export const MAX_SHORT_DAYS = 30
export const MAX_LUMP_DAYS = 365

export const CREDIT_MODES = [
  { value: 'installments', label: 'Parcelado (cada parcela cai a X dias da anterior)' },
  { value: 'lump', label: 'Tudo de uma vez (o valor inteiro cai em X dias)' }
]

export const DEFAULT_SETTLEMENT = { pix: 0, debit: 1, credit: { mode: 'installments', days: 30 } }

// Completa o que faltar com o padrão, sem aliasing com a constante.
export function normalizeSettlement(raw) {
  return {
    pix: raw?.pix ?? DEFAULT_SETTLEMENT.pix,
    debit: raw?.debit ?? DEFAULT_SETTLEMENT.debit,
    credit: {
      mode: raw?.credit?.mode ?? DEFAULT_SETTLEMENT.credit.mode,
      days: raw?.credit?.days ?? DEFAULT_SETTLEMENT.credit.days
    }
  }
}

export const maxCreditDays = mode => (mode === 'lump' ? MAX_LUMP_DAYS : MAX_SHORT_DAYS)

// '' => null (ainda digitando); texto que não é número inteiro não negativo => NaN.
export function parseDays(text) {
  const clean = String(text ?? '').trim()
  if (clean === '') return null

  return /^\d+$/.test(clean) ? Number(clean) : NaN
}

export function findFeeRow(rowId) {
  return [PIX_ROW, ...FEE_ROWS].find(row => row.id === rowId) ?? null
}

// Taxa cobrada pela maquininha sobre `value`. Taxa não informada conta como zero; o líquido é
// o que sobra para o salão. Arredonda a centavos para taxa + líquido fecharem no valor pago.
//
// Vale tanto para a taxa absorvida quanto para a repassada ao cliente: nos dois casos `value`
// já é o valor final que vai para a maquininha (ver `grossUpValue`, chamada só no instante em
// que o salão liga "repassar ao cliente" ou muda a maquininha/bandeira/modalidade com a taxa já
// repassada) — dali em diante a taxa é sempre a mesma % de `value`, do jeito de sempre.
export function calcMachineFee(value, feePercent) {
  const gross = Number(value) || 0
  const percent = Number(feePercent) || 0
  const feeValue = Math.round(gross * percent) / 100

  return { feeValue, netValue: Math.round((gross - feeValue) * 100) / 100 }
}

// Engorda `baseValue` (o preço que o salão quer receber líquido) para o valor a cobrar na
// maquininha, de forma que `calcMachineFee(resultado, feePercent).netValue === baseValue` —
// quem paga a taxa é o cliente, no acréscimo, e o salão recebe o preço cheio. Fora de 0-100% a
// conta não faz sentido (divide por zero ou fica negativa), então devolve `baseValue` sem mudar.
//
// A taxa é arredondada a centavos primeiro, e a cobrança sai de `base + taxa` — não da divisão
// `base / (1 - %)` arredondada por si: arredondar os dois lados separadamente podia fechar um
// centavo torto quando `calcMachineFee` reconferisse a taxa em cima do valor já engordado.
export function grossUpValue(baseValue, feePercent) {
  const base = Number(baseValue) || 0
  const percent = Number(feePercent) || 0

  if (percent <= 0 || percent >= 100) return base

  const feeValue = Math.round((base * percent / (100 - percent)) * 100) / 100
  return Math.round((base + feeValue) * 100) / 100
}

// Campos da linha paga por maquininha ao reabrir uma comanda. Só quem foi salvo com modalidade
// ganha os campos; `card_machine_id` nulo é a "Maquininha sem registro".
export function hydrateMachinePayment(payment, machines = []) {
  // Pix salvo sem modalidade foi recebido sem maquininha.
  if (!payment.modality) {
    return payment.payment_method?.codename === 'pix'
      ? { cardMachine: NO_MACHINE_OPTION, feeChargedToCustomer: payment.fee_charged_to_customer ?? false }
      : { feeChargedToCustomer: payment.fee_charged_to_customer ?? false }
  }

  const machineId = payment.card_machine_id ?? payment.card_machine?.id ?? null
  const machine = machines.find(item => item.id === machineId)
  const row = findFeeRow(payment.modality)

  return {
    cardMachine: { label: machine?.name ?? UNREGISTERED_MACHINE.name, value: machine ? machineId : null },
    // Pix não tem bandeira na tela, mesmo que um Pix antigo tenha sido salvo com uma.
    brand: payment.brand && payment.modality !== PIX_ROW.id
      ? { label: brandLabel(payment.brand), value: payment.brand }
      : null,
    modality: row ? { label: row.label, value: row.id } : null,
    feePercent: payment.fee_percent ?? null,
    feeValue: payment.fee_value ?? null,
    netValue: payment.net_value ?? null,
    feeChargedToCustomer: payment.fee_charged_to_customer ?? false
  }
}

// "Sem maquininha" não é uma maquininha: tira a opção da tela das linhas antes de enviar, para o
// servidor gravar `card_machine_id` nulo e sem bandeira, modalidade ou taxa.
export function withoutMachineSentinel(payments) {
  return payments.map(payment => (
    payment.cardMachine?.value === NO_MACHINE_VALUE ? { ...payment, cardMachine: null } : payment
  ))
}

// A maquininha padrão do salão (só as cadastradas podem ser), ou `undefined` quando não há.
export function getDefaultMachine(machines = []) {
  return machines.find(machine => machine.is_default)
}

// A padrão no formato das opções do campo "Maquininha", para já vir escolhida na comanda.
export function defaultMachineOption(machines = []) {
  const machine = getDefaultMachine(machines)

  return machine ? { label: machine.name, value: machine.id } : null
}
